import { FC, useMemo } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import {
  formatCurrency,
  formatCurrencyDecimals,
  formatNumber,
} from 'utils/formats';
import { TradingOffersSummary, TradingOffer } from 'types/TradingOffer';
import { TradingOfferOperation } from 'types/TradingOfferOperation';

type EquityMarketAdminPanelSummaryCardProps = {
  sellOffersSummary: TradingOffersSummary;
  buyOffersSummary: TradingOffersSummary;
  pendingOperationsCount: number;
  sellOffers?: TradingOffer[];
  buyOffers?: TradingOffer[];
  operations?: TradingOfferOperation[];
};

const PENDING_STATUSES = [
  'PENDING',
  'KYC_REVIEW',
  'VALIDATION',
  'PAYMENT_PENDING',
  'TRANSFER_PENDING',
];

const getPriceStats = (
  prices: number[]
): { avg: number; max: number; min: number } => {
  if (prices.length === 0) return { avg: 0, max: 0, min: 0 };
  const avg = prices.reduce((a, b) => a + b, 0) / prices.length;
  const max = Math.max(...prices);
  const min = Math.min(...prices);
  return { avg, max, min };
};

const PriceStatsLine: FC<{
  avg: number;
  max: number;
  min: number;
}> = ({ avg, max, min }) => {
  const { t } = useTranslate();

  return (
    <p className="text-soft fs-12px mb-0 mt-1">
      {t('AvgPriceShort')}: {formatCurrencyDecimals(avg)} · {t('MaxPriceShort')}
      : {formatCurrencyDecimals(max)} · {t('MinPriceShort')}:{' '}
      {formatCurrencyDecimals(min)}
    </p>
  );
};

const EquityMarketAdminPanelSummaryCard: FC<
  EquityMarketAdminPanelSummaryCardProps
> = ({
  sellOffersSummary,
  buyOffersSummary,
  pendingOperationsCount,
  sellOffers = [],
  buyOffers = [],
  operations = [],
}) => {
  const { t } = useTranslate();

  const sellPriceStats = useMemo(() => {
    const prices = sellOffers.map((o) => o.sharePrice).filter((p) => p > 0);
    return getPriceStats(prices);
  }, [sellOffers]);

  const buyPriceStats = useMemo(() => {
    const prices = buyOffers.map((o) => o.sharePrice).filter((p) => p > 0);
    return getPriceStats(prices);
  }, [buyOffers]);

  const operationsPriceStats = useMemo(() => {
    const pendingOps = operations.filter((op) =>
      PENDING_STATUSES.includes(op.status)
    );
    const prices = pendingOps.map((op) => op.sharePrice).filter((p) => p > 0);
    return getPriceStats(prices);
  }, [operations]);

  return (
    <div className="nk-block">
      <div className="row g-gs">
        <div className="col-sm-4">
          <div
            className="card card-bordered"
            style={{ borderLeft: '3px solid #1ee0ac' }}
          >
            <div className="card-inner py-3">
              <h6 className="overline-title text-soft mb-1">
                {t('SellOffers')}
              </h6>
              <h4 className="mb-1">
                {formatCurrency(sellOffersSummary.totalAmount)}
              </h4>
              <p className="text-soft fs-13px mb-0">
                {sellOffersSummary.offersCount} {t('Offers').toLowerCase()} ·{' '}
                {formatNumber(sellOffersSummary.sharesCount)}{' '}
                {t('Shares').toLowerCase()}
              </p>
              <PriceStatsLine
                avg={sellPriceStats.avg}
                max={sellPriceStats.max}
                min={sellPriceStats.min}
              />
            </div>
          </div>
        </div>

        <div className="col-sm-4">
          <div
            className="card card-bordered"
            style={{ borderLeft: '3px solid #6576ff' }}
          >
            <div className="card-inner py-3">
              <h6 className="overline-title text-soft mb-1">
                {t('BuyOffers')}
              </h6>
              <h4 className="mb-1">
                {formatCurrency(buyOffersSummary.totalAmount)}
              </h4>
              <p className="text-soft fs-13px mb-0">
                {buyOffersSummary.offersCount} {t('Offers').toLowerCase()} ·{' '}
                {formatNumber(buyOffersSummary.sharesCount)}{' '}
                {t('Shares').toLowerCase()}
              </p>
              <PriceStatsLine
                avg={buyPriceStats.avg}
                max={buyPriceStats.max}
                min={buyPriceStats.min}
              />
            </div>
          </div>
        </div>

        <div className="col-sm-4">
          <div
            className="card card-bordered"
            style={{ borderLeft: '3px solid #f4bd0e' }}
          >
            <div className="card-inner py-3">
              <h6 className="overline-title text-soft mb-1">
                {t('PendingOperations')}
              </h6>
              <h4 className="mb-1">{pendingOperationsCount}</h4>
              <p className="text-soft fs-13px mb-0">
                {t('PendingOperationsDescription')}
              </p>
              <PriceStatsLine
                avg={operationsPriceStats.avg}
                max={operationsPriceStats.max}
                min={operationsPriceStats.min}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EquityMarketAdminPanelSummaryCard;
